// src/context/NoteContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

import { Note } from '../types';

// Context value interface
interface NoteContextValue {
  notes: Note[];
  addNote: (note: Omit<Note, 'id' | 'userId' | 'pinned'>) => Promise<void>;
  updateNote: (id: string, note: Partial<Omit<Note, 'id' | 'userId'>>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  refreshNotes: () => Promise<void>;
}

// Create context with default values
const NoteContext = createContext<NoteContextValue | undefined>(undefined);

// Hook for using the context
export const useNote = (): NoteContextValue => {
  const context = useContext(NoteContext);
  if (context === undefined) {
    throw new Error('useNote must be used within a NoteProvider');
  }
  return context;
};

// Provider component
export const NoteProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { user, hasSubscription, isTrial } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = async () => {
    // FIXED: Allow both subscription AND trial users
    if (!user || (!hasSubscription && !isTrial)) {
      setNotes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching notes for user:', user.id);
      
      // FIXED: Changed userId to user_id to match database column name
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Map database snake_case to camelCase for frontend
      const mappedNotes = data?.map(note => ({
        id: note.id,
        userId: note.user_id,
        title: note.title,
        content: note.content,
        imageUrl: note.image_url,
        pinned: note.pinned || false,
        created_at: note.created_at,
        updated_at: note.updated_at
      })) || [];

      setNotes(mappedNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch notes when the user changes
  useEffect(() => {
    fetchNotes();
  }, [user, hasSubscription, isTrial]);

  const addNote = async (note: Omit<Note, 'id' | 'userId' | 'pinned'>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // FIXED: Use snake_case property names to match database columns
      const { error, data } = await supabase
        .from('notes')
        .insert({
          title: note.title,
          content: note.content,
          image_url: note.imageUrl,
          user_id: user.id,
          pinned: false
        })
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      // Map database response to frontend format
      const newNote: Note = {
        id: data.id,
        userId: data.user_id,
        title: data.title,
        content: data.content,
        imageUrl: data.image_url,
        pinned: data.pinned || false,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setNotes(prev => {
        const updatedNotes = [newNote, ...prev];
        return updatedNotes.sort((a, b) => {
          if (a.pinned !== b.pinned) {
            return b.pinned ? 1 : -1;
          }
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        });
      });
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  };

  const updateNote = async (id: string, note: Partial<Omit<Note, 'id' | 'userId'>>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // FIXED: Use snake_case property names to match database columns
      const updateData: any = {};
      if (note.title !== undefined) updateData.title = note.title;
      if (note.content !== undefined) updateData.content = note.content;
      if (note.imageUrl !== undefined) updateData.image_url = note.imageUrl;
      if (note.pinned !== undefined) updateData.pinned = note.pinned;

      const { error, data } = await supabase
        .from('notes')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      // Map database response to frontend format
      const updatedNote: Note = {
        id: data.id,
        userId: data.user_id,
        title: data.title,
        content: data.content,
        imageUrl: data.image_url,
        pinned: data.pinned || false,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setNotes(prev => {
        const updatedNotes = prev.map(n => (n.id === id ? updatedNote : n));
        return updatedNotes.sort((a, b) => {
          if (a.pinned !== b.pinned) {
            return b.pinned ? 1 : -1;
          }
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        });
      });
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  };

  const togglePin = async (id: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const noteToUpdate = notes.find(n => n.id === id);
      if (!noteToUpdate) {
        throw new Error('Note not found');
      }

      const newPinnedState = !noteToUpdate.pinned;

      const { error, data } = await supabase
        .from('notes')
        .update({ pinned: newPinnedState })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Toggle pin error:', error);
        throw error;
      }

      // Map database response to frontend format
      const updatedNote: Note = {
        id: data.id,
        userId: data.user_id,
        title: data.title,
        content: data.content,
        imageUrl: data.image_url,
        pinned: data.pinned || false,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setNotes(prev => {
        const updatedNotes = prev.map(n => (n.id === id ? updatedNote : n));
        return updatedNotes.sort((a, b) => {
          if (a.pinned !== b.pinned) {
            return b.pinned ? 1 : -1;
          }
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        });
      });
    } catch (error) {
      console.error('Error toggling pin:', error);
      throw error;
    }
  };

  const deleteNote = async (id: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // FIXED: Changed userId to user_id to match database column name
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }

      setNotes(prev => prev.filter(note => note.id !== id));
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  };

  const refreshNotes = async () => {
    await fetchNotes();
  };

  return (
    <NoteContext.Provider value={{ 
      notes, 
      addNote, 
      updateNote, 
      deleteNote,
      togglePin,
      loading, 
      error,
      refreshNotes 
    }}>
      {children}
    </NoteContext.Provider>
  );
};